import {
  Close,
  Email,
  MouseOutlined,
  VisibilityOutlined,
  AccessTime,
  ExpandMore,
  ExpandLess,
  Person,
  PersonAdd,
  Message,
  Work,
  LinkedIn,
  PersonSearch,
  ConnectWithoutContact,
  Sms,
  Event,
  EventAvailable,
  MarkunreadMailbox,
} from "@mui/icons-material";
import { Bot } from "lucide-react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Collapse,
  Button,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import DOMPurify from "dompurify";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import {
  AUTO_AGENT_TRIGGER_INITIAL,
  AUTO_AGENT_TRIGGER_REACTIVE,
  AUTO_AGENT_TRIGGER_SCHEDULED,
} from "../../constants";

interface TimelineEvent {
  type: string;
  timestamp: string;
  subject?: string;
  executionId?: string;
  stepName?: string;
  content?: string;
  openCount?: number;
  clickCount?: number;
  ipAddress?: string;
  userAgent?: string;
  platform?: string;
  actionType?: string;
  // Auto agent fields
  isAutoAgent?: boolean;
  autoAgentTriggerType?: string;
  autoAgentChannel?: string;
  autoAgentReasoning?: string;
  autoAgentStepNumber?: number;
  autoAgentInitialMessage?: boolean;
}

interface SequenceMemberTimelineDialogProps {
  open: boolean;
  onClose: () => void;
  member: any;
  sequence: any;
  companyId: string;
  getToken: Function;
}

export default function SequenceMemberTimelineDialog({
  open,
  onClose,
  member,
  sequence,
  companyId,
  getToken,
}: SequenceMemberTimelineDialogProps) {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [expandedContent, setExpandedContent] = useState<{ [key: string]: boolean }>({});

  const loadTimeline = async () => {
    if (!member || !sequence) return;

    setLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(
        `${hostname}/sequences/${sequence.id}/members/${member.id}/timeline`,
        {
          params: { companyId },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setTimeline(response.data.timeline);
    } catch (error) {
      console.error("Error loading timeline:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && member && sequence) {
      loadTimeline();
    }
  }, [open, member, sequence]);

  const getEventIcon = (type: string) => {
    switch (type) {
      case "email_sent":
        return <Email color="primary" />;
      case "email_opened":
      case "tracking_open":
        return <VisibilityOutlined color="primary" />;
      case "email_clicked":
      case "tracking_click":
        return <MouseOutlined color="info" />;
      case "sms_sent":
        return <Sms color="primary" />;
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return <PersonSearch color="secondary" />;
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return <ConnectWithoutContact color="warning" />;
      case "linkedin_message":
      case "linkedin_message_sent":
        return <Message color="info" />;
      case "linkedin_inmails":
      case "inmail_executed":
        return <Email color="secondary" />;
      case "Schedule View":
      case "schedule_view":
        return <Event color="info" />;
      case "Schedule Booking":
      case "schedule_booking":
        return <EventAvailable color="primary" />;
      case "thanksio_executed":
      case "thanksio":
        return <MarkunreadMailbox color="primary" />;
      default:
        return type.startsWith("linkedin_") ? <LinkedIn color="primary" /> : <AccessTime />;
    }
  };

  const getEventLabel = (type: string) => {
    switch (type) {
      case "email_sent":
        return "Email Sent";
      case "email_opened":
        return "Email Opened";
      case "email_clicked":
        return "Email Clicked";
      case "sms_sent":
        return "SMS Sent";
      case "tracking_open":
        return "Opened";
      case "tracking_click":
        return "Clicked Link";
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return "Profile Visit";
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return "Connection Request";
      case "linkedin_message":
      case "linkedin_message_sent":
        return "Message Sent";
      case "linkedin_inmails":
      case "inmail_executed":
        return "InMail Sent";
      case "Schedule View":
      case "schedule_view":
        return "Viewed Meeting Page";
      case "Schedule Booking":
      case "schedule_booking":
        return "Booked Meeting";
      case "thanksio_executed":
      case "thanksio":
        return "Thanks.io Direct Mail";
      default:
        if (type.endsWith("_executed")) {
          return type.replace("_executed", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
        }
        return type.startsWith("linkedin_") ?
          type.replace("linkedin_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) :
          type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getEventColor = (type: string): any => {
    switch (type) {
      case "email_sent":
        return "primary";
      case "email_opened":
      case "tracking_open":
        return "primary";
      case "email_clicked":
      case "tracking_click":
        return "info";
      case "sms_sent":
        return "primary";
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return "secondary";
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return "warning";
      case "linkedin_message":
      case "linkedin_message_sent":
      case "linkedin_inmails":
      case "inmail_executed":
        return "info";
      case "Schedule View":
      case "schedule_view":
        return "info";
      case "Schedule Booking":
      case "schedule_booking":
        return "primary";
      case "thanksio_executed":
      case "thanksio":
        return "primary";
      default:
        return type.startsWith("linkedin_") ? "primary" : "default";
    }
  };

  const getAutoAgentTriggerLabel = (triggerType?: string): string => {
    switch (triggerType) {
      case AUTO_AGENT_TRIGGER_INITIAL:
        return "Initial outreach";
      case AUTO_AGENT_TRIGGER_REACTIVE:
        return "Response to contact reply";
      case AUTO_AGENT_TRIGGER_SCHEDULED:
        return "Scheduled follow-up";
      default:
        return "Auto Agent message";
    }
  };

  const toggleContent = (executionId: string) => {
    setExpandedContent((prev) => ({
      ...prev,
      [executionId]: !prev[executionId],
    }));
  };

  const renderContent = (content: string, type: string) => {
    // For SMS and LinkedIn messages, render as plain text
    if (type === "sms_sent" || type === "linkedin_message_sent") {
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            backgroundColor: "grey.50",
            whiteSpace: "pre-wrap",
          }}
        >
          <Typography variant="body2">{content}</Typography>
        </Box>
      );
    }
    
    // For emails, render as HTML
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          "& img": { maxWidth: "100%" },
          "& a": { color: "primary.main" },
        }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6">
              Timeline for {member?.firstName} {member?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {member?.email}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!loading && timeline.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No timeline events found for this member.
          </Typography>
        )}
        {!loading && timeline.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableBody>
                {timeline.map((event, index) => (
                  <>
                    <TableRow key={`${event.executionId}-${index}`}>
                      <TableCell width={50}>
                        {getEventIcon(event.type)}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              label={getEventLabel(event.type)}
                              size="small"
                              color={getEventColor(event.type)}
                            />
                            {event.isAutoAgent && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {getAutoAgentTriggerLabel(event.autoAgentTriggerType)}
                                    </Typography>
                                    {event.autoAgentStepNumber && (
                                      <Typography variant="caption" display="block">
                                        Follow-up #{event.autoAgentStepNumber}
                                      </Typography>
                                    )}
                                    {event.autoAgentReasoning && (
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
                                        {event.autoAgentReasoning}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              >
                                <Chip
                                  icon={<Bot size={16} />}
                                  label={`Auto Agent${event.autoAgentChannel ? ` · ${event.autoAgentChannel}` : ""}`}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ textTransform: "capitalize" }}
                                />
                              </Tooltip>
                            )}
                            <Typography variant="body2" fontWeight={500}>
                              {event.stepName}
                            </Typography>
                          </Box>
                          {event.subject && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {event.subject}
                            </Typography>
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                            <Typography variant="caption" color="text.secondary">
                              {moment(event.timestamp).format("MMM D, YYYY h:mm A")}
                            </Typography>
                            {event.openCount && event.openCount > 1 && (
                              <Typography variant="caption" color="primary.main">
                                Opened {event.openCount} times
                              </Typography>
                            )}
                            {event.clickCount && event.clickCount > 1 && (
                              <Typography variant="caption" color="info.main">
                                Clicked {event.clickCount} times
                              </Typography>
                            )}
                            {event.ipAddress && (
                              <Typography variant="caption" color="text.secondary">
                                IP: {event.ipAddress}
                              </Typography>
                            )}
                          </Box>
                          {event.content && (event.type === "email_sent" || event.type === "sms_sent" || event.type === "linkedin_message_sent") && (
                            <Button
                              size="small"
                              onClick={() => toggleContent(event.executionId!)}
                              startIcon={
                                expandedContent[event.executionId!] ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              {expandedContent[event.executionId!]
                                ? "Hide Content"
                                : "View Content"}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {event.content && event.executionId && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Collapse in={expandedContent[event.executionId]}>
                            {renderContent(event.content, event.type)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
